'use client';

import { CustomEventName, GTMConstants, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { styled } from '@linaria/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const COMPOSITION = {
  START: 'start',
  END: 'end'
};

const GTM_EVENT = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: GTMConstants.SECTION_PLACEHOLDER,
  [GTMEventKey.CLICK_ITEM]: 'Search_News'
};

const View = styled.div``;

const Button = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;

  h3 {
    margin-left: 8px;
    font-size: 18px;
    color: #848d97;

    :hover {
      color: #515c69;
    }
  }
`;

const SearchIcon = styled.button`
  cursor: pointer;
`;

const SearchButton = styled.button<{ isActive: boolean }>`
  cursor: pointer;
  padding: 4px 14px;
  border-radius: 6px;
  opacity: ${({ isActive }) => (isActive ? '1' : '0.5')};
  background-color: ${({ isActive }) => (isActive ? '#3ec1ce' : '#848d97')};
  box-shadow: ${({ isActive }) => (isActive ? 'inset 0 0 0 1px #3ec1ce, 0 0 0 2px #c1f6ff' : 'none')};
  margin-left: 24px;
`;

const Container = styled.div<{ open?: boolean }>`
  position: absolute;
  height: 48px;
  background-color: #f2f4f8;
  border-radius: 6px;
  padding: 12px 8px 12px 12px;
  display: flex;
  align-items: center;
  width: ${({ open }) => (open ? '100%' : '300px')};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  opacity: ${({ open }) => (open ? '1' : '0')};
  transition:
    opacity 0.3s ease,
    width 0.5s ease,
    visibility 2s ease;
  top: 0;
  right: 0;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    top: -12px;
  }
`;

const Input = styled.input`
  color: #515c69;
  font-size: 18px;
  flex: 1px;
  margin-left: 8px;
`;

const Span = styled.span`
  font-size: 18px;
  color: #848d97;
  margin-left: 8px;
  letter-spacing: normal;
`;

const DELAY_AUTO_FOCUS_TIME = 750;

const NewsSearch = () => {
  const router = useRouter();
  const inputReference = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isShowClose, setIsShowClose] = useState<boolean>(false);

  // 解決使用輸入法在 React Input 輸入框中輸入的問題
  // https://juejin.cn/post/6861098174723915790
  let compositionStatus = COMPOSITION.END;

  const changeSearchInputOpenOrHide = () => {
    setIsOpen(v => !v);
  };

  const onClickSearchBar = () => {
    if (!isOpen) {
      sendGTM(GTM_EVENT);
    }

    changeSearchInputOpenOrHide();
  };

  useEffect(() => {
    if (isOpen && inputReference && inputReference.current) {
      inputReference.current.value = '';

      // 解決在手機上點擊搜尋按鈕後，無法自動 focus 的問題
      setTimeout(() => {
        inputReference.current?.focus();
      }, DELAY_AUTO_FOCUS_TIME);
    }
  }, [isOpen]);

  const handleSearch = () => {
    const keyword = inputReference.current?.value;

    if (keyword) {
      sendGTM({
        [GTMEventKey.EVENT_NAME]: CustomEventName.SEARCH,
        [GTMEventKey.SECTION]: '%p',
        [GTMEventKey.CLICK_ITEM]: keyword
      });

      router.push(`/search?q=${keyword}`);
      changeSearchInputOpenOrHide();
    }
  };

  const onClickSearch = () => {
    handleSearch();
  };

  const onClickSearchClear = () => {
    setIsShowClose(false);
    onClickSearchBar();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // 搜尋中文時避免按Enter後馬上送出(中文會有Enter選字問題)
    if (compositionStatus !== COMPOSITION.END) return;

    // 在 Android 裝置上，當使用者點擊鍵盤上的搜尋鍵時，會觸發一個 keyCode 為 13 的事件。所以你可以修改 onKeyDown 函式來處理這個事件：
    if (event.keyCode === 13) {
      handleSearch();
    }
  };

  const onChange = () => {
    if (inputReference && inputReference.current && inputReference.current?.value === '') {
      setIsShowClose(false);
    } else {
      setIsShowClose(true);
    }
  };

  const onCompositionChangeHandler = (status: string) => (): void => {
    compositionStatus = status;
  };

  return (
    <View>
      <Button onClick={onClickSearchBar}>
        <Image src='/assets/icons/search/search-gray.svg' width={24} height={24} alt='search icon' unoptimized />
        <Span>搜尋新聞</Span>
      </Button>

      <Container open={isOpen}>
        <Input
          ref={inputReference}
          type='search'
          placeholder='請輸入關鍵字'
          onCompositionStart={onCompositionChangeHandler(COMPOSITION.START)}
          onCompositionEnd={onCompositionChangeHandler(COMPOSITION.END)}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onBlur={onClickSearchClear}
        />
        {isShowClose && (
          <SearchIcon onClick={onClickSearchClear}>
            <Image
              src='/assets/icons/close/search-close.svg'
              width={24}
              height={24}
              alt='close search icon'
              unoptimized
            />
          </SearchIcon>
        )}
        <SearchButton onClick={onClickSearch} isActive={isShowClose} disabled={!isShowClose}>
          <Image src='/assets/icons/search/search-white.svg' width={24} height={24} alt='search button' unoptimized />
        </SearchButton>
      </Container>
    </View>
  );
};

export default NewsSearch;
